import { Link, NavLink, useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { Menu, X, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import NotificationsDropdown from "@/components/NotificationsDropdown";
import LogoMark from "@/components/LogoMark";
import AdminControlCenter from "@/components/AdminControlCenter";

const linkBase =
  "text-xs uppercase tracking-[0.22em] font-medium text-white/80 hover:text-white transition-colors duration-300";

const linkActive = "text-white";

export default function Nav() {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const [open, setOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const bellAnchorRef = useRef(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  const items = [
    { to: "/", label: "Home" },
    { to: "/fleet", label: "Fleet" },
    { to: "/pricing", label: "Rates" },
    { to: "/book", label: "Book" },
  ];

  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B0B0C]/70 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-brand-link"
          className="flex items-center group"
        >
          <LogoMark size="md" />
        </Link>

        {/* Desktop */}
        <nav className="hidden md:flex items-center gap-10">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-link-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : ""}`
              }
              end={it.to === "/"}
            >
              {it.label}
            </NavLink>
          ))}
          {user ? (
            <>
              <NavLink
                to="/account"
                data-testid="nav-link-account"
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? linkActive : ""}`
                }
              >
                Account
              </NavLink>
              <AdminControlCenter role={user.role} />
              <div className="relative" ref={bellAnchorRef}>
                <button
                  type="button"
                  onClick={() => setBellOpen((o) => !o)}
                  data-testid="nav-notifications-bell"
                  aria-label={`Notifications (${unreadCount} unread)`}
                  aria-expanded={bellOpen}
                  className="relative flex items-center text-white/80 hover:text-white transition-colors p-1 -m-1"
                >
                  <Bell size={16} strokeWidth={1.5} />
                  {unreadCount > 0 && (
                    <span
                      data-testid="nav-notifications-badge"
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] flex items-center justify-center bg-white text-black font-medium tracking-tight"
                    >
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                {bellOpen && (
                  <NotificationsDropdown
                    onClose={() => setBellOpen(false)}
                    anchorRef={bellAnchorRef}
                  />
                )}
              </div>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className={linkBase}
              >
                Logout
              </button>
            </>
          ) : (
            <NavLink
              to="/login"
              data-testid="nav-link-login"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : ""}`
              }
            >
              Login
            </NavLink>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-white/10 bg-[#0B0B0C]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? linkActive : ""}`
                }
                end={it.to === "/"}
              >
                {it.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <NavLink
                  to="/account"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-link-account"
                  className={linkBase}
                >
                  Account
                </NavLink>
                <button
                  onClick={handleLogout}
                  data-testid="nav-mobile-logout"
                  className={`${linkBase} text-left`}
                >
                  Logout
                </button>
              </>
            ) : (
              <NavLink
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-login"
                className={linkBase}
              >
                Login
              </NavLink>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
